/**
 * useOfflineMutation Hook
 *
 * A wrapper around React Query's useMutation that provides offline support.
 * When offline, mutations are queued to IndexedDB and synced when back online.
 * Supports optimistic updates for immediate UI feedback.
 */

import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient, UseMutationOptions, UseMutationResult } from 'react-query';

import { addMutation } from './mutationQueue';
import { MutationOperationType } from './types';
import { isCurrentlyOnline, useOnlineStatus } from './useOnlineStatus';
import { usePendingMutationCount } from './useMutationQueue';

/**
 * Check if an error is likely a network error
 * This helps detect when we've gone offline during a request
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    // fetch throws TypeError for network failures
    const message = error.message.toLowerCase();
    return message.includes('network') || message.includes('fetch') || message.includes('failed to fetch');
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    const name = error.name.toLowerCase();
    // Common network error patterns
    return (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('econnrefused') ||
      message.includes('enotfound') ||
      message.includes('failed to fetch') ||
      message.includes('net::err') ||
      message.includes('ns_error_offline') ||
      name.includes('networkerror') ||
      // Axios error patterns
      message.includes('network error') ||
      message.includes('request failed')
    );
  }

  // Check for axios error structure
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    return true;
  }

  return false;
}

export type UseOfflineMutationOptions<TData, TError, TVariables> = {
  /**
   * The mutation function to execute when online
   */
  mutationFn: (variables: TVariables) => Promise<TData>;

  /**
   * The mutation name for the batch API (e.g., 'createFiber', 'patchODF')
   */
  mutationName: string;

  /**
   * Full GraphQL document for non-batchable mutations.
   * Required if the mutation is not in BATCHABLE_MUTATIONS.
   * This allows the mutation to be sent to its original endpoint when syncing.
   */
  graphqlDocument?: string;

  /**
   * Entity type being mutated (e.g., 'fiber', 'outlet', 'premise')
   */
  entity: string;

  /**
   * Type of operation
   */
  operationType: MutationOperationType;

  /**
   * Property ID for grouping mutations (optional for non-property operations)
   */
  propertyId?: number;

  /**
   * Query keys to invalidate after successful mutation
   */
  invalidateKeys?: readonly (readonly unknown[])[];

  /**
   * Generate optimistic data to apply immediately while offline
   * Return the data that should be used for optimistic updates
   */
  getOptimisticData?: (variables: TVariables) => unknown;

  /**
   * Apply optimistic update to the cache
   * Called with the optimistic data when mutation is queued offline
   */
  applyOptimisticUpdate?: (optimisticData: unknown) => void;

  /**
   * Rollback optimistic update on error
   * Called when online mutation fails
   */
  rollbackOptimisticUpdate?: () => void;

  /**
   * Callback when mutation is queued for offline sync
   */
  onOfflineQueued?: (mutationId: string) => void;

  /**
   * Standard React Query mutation options
   */
  onSuccess?: UseMutationOptions<TData, TError, TVariables>['onSuccess'];
  onError?: UseMutationOptions<TData, TError, TVariables>['onError'];
  onSettled?: UseMutationOptions<TData, TError, TVariables>['onSettled'];
};

export type UseOfflineMutationResult<TData, TError, TVariables> = UseMutationResult<TData, TError, TVariables> & {
  /**
   * Whether the app is currently online
   */
  isOnline: boolean;

  /**
   * Number of pending mutations in the queue
   */
  pendingCount: number;

  /**
   * Whether the last mutation was queued offline
   */
  wasQueuedOffline: boolean;
};

/**
 * Hook for mutations that work offline
 *
 * @example
 * ```tsx
 * const { mutate, isLoading, isOnline, wasQueuedOffline } = useOfflineMutation({
 *   mutationFn: (variables) => requestGQL(PATCH_FIBER, variables),
 *   mutationName: 'patchFiber',
 *   entity: 'fiber',
 *   operationType: 'UPDATE',
 *   propertyId: 123,
 *   invalidateKeys: [['propertyConnection', 123]],
 *   onOfflineQueued: () => toast.info('Changes saved locally'),
 * });
 *
 * // Works the same whether online or offline
 * mutate({ fiberId: 'abc', length: 100 });
 * ```
 */
export function useOfflineMutation<TData = unknown, TError = unknown, TVariables = unknown>(
  options: UseOfflineMutationOptions<TData, TError, TVariables>,
): UseOfflineMutationResult<TData, TError, TVariables> {
  const {
    mutationFn,
    mutationName,
    graphqlDocument,
    entity,
    operationType,
    propertyId,
    invalidateKeys,
    getOptimisticData,
    applyOptimisticUpdate,
    rollbackOptimisticUpdate,
    onOfflineQueued,
    onSuccess,
    onError,
    onSettled,
  } = options;

  const queryClient = useQueryClient();
  const { isOnline } = useOnlineStatus();
  const pendingCount = usePendingMutationCount();

  // Track if last mutation was queued offline using state so the returned value updates after mutation
  const [wasQueuedOffline, setWasQueuedOffline] = useState(false);

  // The actual mutation logic
  const mutation = useMutation<TData, TError, TVariables>(
    async (variables) => {
      // Check online status at mutation time
      if (!isCurrentlyOnline()) {
        // We're offline - queue the mutation
        const optimisticData = getOptimisticData?.(variables);

        // Store the payload as-is - callers are responsible for providing
        // the correct format expected by the batch API: { input: { propertyId, ... } }
        // This ensures consistency between online and offline mutation paths
        const queuedMutation = await addMutation({
          type: operationType,
          entity,
          payload: variables,
          mutationName,
          graphqlDocument,
          propertyId,
          invalidateKeys,
        });

        // Apply optimistic update if provided
        if (optimisticData && applyOptimisticUpdate) {
          applyOptimisticUpdate(optimisticData);
        }

        setWasQueuedOffline(true);
        onOfflineQueued?.(queuedMutation.id);

        // Return a "fake" success response for offline mutations
        // The actual sync will happen later
        // eslint-disable-next-line @typescript-eslint/naming-convention
        return { offlineQueued: true, mutationId: queuedMutation.id } as unknown as TData;
      }

      // We're online - execute normally, but handle network errors gracefully
      setWasQueuedOffline(false);
      try {
        return await mutationFn(variables);
      } catch (error) {
        // If we get a network error, queue the mutation for later sync
        // This handles cases where:
        // 1. We went offline during the request (race condition)
        // 2. navigator.onLine incorrectly reports true but network is actually unavailable
        if (isNetworkError(error)) {
          const optimisticData = getOptimisticData?.(variables);

          const queuedMutation = await addMutation({
            type: operationType,
            entity,
            payload: variables,
            mutationName,
            graphqlDocument,
            propertyId,
            invalidateKeys,
          });

          // Apply optimistic update if provided
          if (optimisticData && applyOptimisticUpdate) {
            applyOptimisticUpdate(optimisticData);
          }

          setWasQueuedOffline(true);
          onOfflineQueued?.(queuedMutation.id);

          return { offlineQueued: true, mutationId: queuedMutation.id } as unknown as TData;
        }

        // Re-throw for other errors (non-network errors)
        throw error;
      }
    },
    {
      onSuccess: (data, variables, context) => {
        // Don't invalidate if this was an offline-queued mutation
        // (it will be invalidated when actually synced)
        if (!(data as unknown as { offlineQueued?: boolean })?.offlineQueued) {
          // Invalidate specified query keys
          if (invalidateKeys) {
            invalidateKeys.forEach((key) => {
              queryClient.invalidateQueries(key);
            });
          }
        }

        onSuccess?.(data, variables, context);
      },
      onError: (error, variables, context) => {
        // Rollback optimistic update on error
        rollbackOptimisticUpdate?.();
        onError?.(error, variables, context);
      },
      onSettled,
    },
  );

  // Enhanced mutate that checks for pending sync
  const enhancedMutate = useCallback(
    (variables: TVariables) => {
      mutation.mutate(variables);
    },
    [mutation],
  );

  // Enhanced mutateAsync
  const enhancedMutateAsync = useCallback(
    async (variables: TVariables) => {
      return mutation.mutateAsync(variables);
    },
    [mutation],
  );

  return useMemo(
    () => ({
      ...mutation,
      mutate: enhancedMutate,
      mutateAsync: enhancedMutateAsync,
      isOnline,
      pendingCount,
      wasQueuedOffline,
    }),
    [mutation, enhancedMutate, enhancedMutateAsync, isOnline, pendingCount, wasQueuedOffline],
  );
}
